import { SourSocketClient } from '@wayste/sour-socket';
import { apm } from '@elastic/apm-rum';
import * as Sentry from '@sentry/browser';
import axios, { isAxiosError } from 'axios';
import { AppAdminService } from '../services/AppAdmin/app-admin.service';
import AuditLogService from '../services/AuditLog/AuditLog.service';
import { AuthService } from '../services/Auth/auth.service';
import CommercialService from '../services/Commercial/Commercial.service';
import CustomerService from '../services/Customer/Customer.service';
import DisposalSiteService from '../services/DisposalSite/DisposalSite.service';
import EquipmentService from '../services/Equipment/Equipment.service';
import HaulerService from '../services/Hauler/Hauler.service';
import InternalTicketService from '../services/InternalTicket/InternalTicket.service';
import InvoiceService from '../services/Invoice/Invoice.service';
import NotificationService from '../services/Notification/Notification.service';
import OperationsService from '../services/Operations/Operations.service';
import OrderService from '../services/Order/Order.service';
import { StripeService } from '../services/Payment/Stripe.Service';
import PricingService from '../services/Pricing/Pricing.service';
import ProfileService from '../services/Profile/Profile.service';
import QuickBooksService from '../services/QuickBooks/QuickBooks.service';
import { StripeService as Stripe2Service } from '../services/Stripe/Stripe.service';
import { WaysteStripeService } from '../services/Stripe/WaysteStripe.service';
import TimeSheetService from '../services/TimeSheet/TimeSheet.service';
import UniversalService from '../services/UniversalService/Universal.service';
import Upload from '../services/Upload/Upload.service';
import UserService from '../services/User/User.service';
import VendorService from '../services/Vendor/Vendor.service';
export class WaysteClient {
    static AppAdminService;
    static AuditLogService;
    static AuthService;
    static CommercialService;
    static Customer;
    static DisposalSiteService;
    static EquipmentService;
    static Hauler;
    static InternalTicketService;
    static InvoiceService;
    static NotificationService;
    static Order;
    static PricingService;
    static ProfileService;
    static QuickBooksService;
    static stripe;
    static StripeService;
    static TimeSheetService;
    static UniversalService;
    static Upload;
    static UserService;
    static VendorService;
    static WaysteStripeService;
    static OperationsService;
    static axios;
    options;
    constructor(options) {
        this.options = options;
        WaysteClient.axios = {
            mainInstance: axios.create({
                baseURL: options.API.baseApiUrl,
            }),
            appAdminInstance: axios.create({
                baseURL: options.API.baseApiUrl,
            }),
            socket: new SourSocketClient(options.API.baseApiUrl),
        };
        WaysteClient.axios.mainInstance.interceptors.response.use((response) => response, (error) => {
            Sentry.captureException(error);
            console.error(error);
            if (error && isAxiosError(error)) {
                if (error.response?.data?.code === '23505') {
                    error.response.data.additionalInfo = 'Duplicate entry';
                }
            }
            if (apm.isActive()) {
                apm.captureError(error);
            }
            throw error;
        });
        this.setupServices();
    }
    // Should only run after auth is setup
    setupServices() {
        WaysteClient.AuthService = new AuthService(WaysteClient.axios, this.options);
        WaysteClient.UserService = new UserService(WaysteClient.AuthService, this.options);
        WaysteClient.ProfileService = new ProfileService(WaysteClient.AuthService, WaysteClient.axios, this.options);
        WaysteClient.InvoiceService = new InvoiceService(WaysteClient.axios);
        WaysteClient.Hauler = new HaulerService(WaysteClient.axios);
        if (this.options) {
            WaysteClient.OperationsService = new OperationsService(WaysteClient.axios);
            WaysteClient.StripeService = new StripeService(WaysteClient.AuthService, this.options);
            WaysteClient.stripe = new Stripe2Service(WaysteClient.AuthService, WaysteClient.axios, this.options);
            WaysteClient.VendorService = new VendorService(WaysteClient.AuthService, WaysteClient.axios, this.options);
            WaysteClient.AppAdminService = new AppAdminService(WaysteClient.AuthService, this.options);
            WaysteClient.AuditLogService = new AuditLogService(this.auth(), this.options);
            WaysteClient.CommercialService = new CommercialService(WaysteClient.axios);
            WaysteClient.Customer = new CustomerService(WaysteClient.axios);
            WaysteClient.EquipmentService = new EquipmentService(WaysteClient.axios);
            WaysteClient.Order = new OrderService(WaysteClient.axios);
            WaysteClient.PricingService = new PricingService(WaysteClient.axios);
            WaysteClient.QuickBooksService = new QuickBooksService(WaysteClient.axios);
            WaysteClient.TimeSheetService = new TimeSheetService(this.auth(), this.options);
            WaysteClient.UniversalService = new UniversalService(WaysteClient.axios);
            WaysteClient.Upload = new Upload(WaysteClient.AuthService, WaysteClient.axios, this.options);
            WaysteClient.WaysteStripeService = new WaysteStripeService(WaysteClient.AuthService, this.options);
        }
        WaysteClient.DisposalSiteService = new DisposalSiteService(WaysteClient.axios);
        WaysteClient.InternalTicketService = new InternalTicketService(WaysteClient.axios);
        WaysteClient.NotificationService = new NotificationService(WaysteClient.axios);
    }
    order() {
        return WaysteClient.Order;
    }
    hauler() {
        return WaysteClient.Hauler;
    }
    invoice() {
        return WaysteClient.InvoiceService;
    }
    customer() {
        return WaysteClient.Customer;
    }
    user() {
        return WaysteClient.UserService;
    }
    commercial() {
        return WaysteClient.CommercialService;
    }
    equipment() {
        return WaysteClient.EquipmentService;
    }
    profile() {
        return WaysteClient.ProfileService;
    }
    disposalSite() {
        return WaysteClient.DisposalSiteService;
    }
    auth() {
        return WaysteClient.AuthService;
    }
    internalTicket() {
        return WaysteClient.InternalTicketService;
    }
    notification() {
        return WaysteClient.NotificationService;
    }
    /**
     *
     * @@deprecated - use stripe instead
     */
    stripeService() {
        return WaysteClient.StripeService;
    }
    stripe() {
        return WaysteClient.stripe;
    }
    waysteStripe() {
        return WaysteClient.WaysteStripeService;
    }
    vendorService() {
        return WaysteClient.VendorService;
    }
    appAdmin() {
        return WaysteClient.AppAdminService;
    }
    timeSheet() {
        return WaysteClient.TimeSheetService;
    }
    quickBooks() {
        return WaysteClient.QuickBooksService;
    }
    universalService() {
        return WaysteClient.UniversalService;
    }
    upload() {
        return WaysteClient.Upload;
    }
    pricing() {
        return WaysteClient.PricingService;
    }
    auditLog() {
        return WaysteClient.AuditLogService;
    }
    operations() {
        return WaysteClient.OperationsService;
    }
}
//# sourceMappingURL=WaysteClient.js.map